/* # TopicDetail Page
   - Displays the full details of a forum topic
   - Updated max-width to allow the content and replies to use more screen width
*/
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { LuArrowLeft, LuPin, LuLock, LuTrash2, LuMessageSquare, LuUser, LuClock, LuTag } from 'react-icons/lu';
import { format } from 'date-fns';
import { el, enUS } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { isManager } from '../utils/roleCheck';
import { toast } from '../hooks/use-toast';
import ReplySection from '../components/Forum/ReplySection';
import ReplyForm from '../components/Forum/ReplyForm';
import MentionText from '../components/common/MentionText';

export default function TopicDetail() {
  const { topicId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const [topic, setTopic] = useState(null);
  const [authorName, setAuthorName] = useState(t('faultReports.unknownUser'));
  const [loading, setLoading] = useState(true);

  // Get current date locale
  const dateLocale = i18n.language === 'el' ? el : enUS;

  useEffect(() => {
    loadTopic();
  }, [topicId]);

  const loadTopic = async () => {
    setLoading(true);
    try {
      const { data, error } = await window.ezsite.apis.tablePage(70652, {
        PageNo: 1,
        PageSize: 1,
        Filters: [{ name: 'id', op: 'Equal', value: Number(topicId) }]
      });

      if (error) throw error;
      if (data?.List?.[0]) {
        setTopic(data.List[0]);
        loadAuthor(data.List[0].author_id);
      }
    } catch (error) {
      toast({ title: t('common.error'), description: t('forum.topicNotFound'), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const loadAuthor = async (authorId) => {
    try {
      const { data } = await window.ezsite.apis.tablePage(70648, {
        PageNo: 1,
        PageSize: 1,
        Filters: [{ name: 'id', op: 'Equal', value: authorId }]
      });
      if (data?.List?.[0]) {
        setAuthorName(data.List[0].name);
      }
    } catch (e) {console.error(e);}
  };

  const handlePin = async () => {
    try {
      await window.ezsite.apis.tableUpdate(70652, { id: topic.id, is_pinned: !topic.is_pinned });
      toast({
        title: t('common.success'),
        description: topic.is_pinned ? t('forum.topicUnpinned') : t('forum.topicPinned')
      });
      loadTopic();
    } catch (e) {
      toast({ title: t('common.error'), description: t('forum.updateFailed'), variant: 'destructive' });
    }
  };

  const handleLock = async () => {
    try {
      await window.ezsite.apis.tableUpdate(70652, { id: topic.id, is_locked: !topic.is_locked });
      toast({
        title: t('common.success'),
        description: topic.is_locked ? t('forum.topicUnlocked') : t('forum.topicLocked')
      });
      loadTopic();
    } catch (e) {
      toast({ title: t('common.error'), description: t('forum.updateFailed'), variant: 'destructive' });
    }
  };

  const handleDelete = async () => {
    if (!confirm(t('forum.deleteConfirm'))) return;
    try {
      await window.ezsite.apis.tableDelete(70652, { ID: topic.id });
      toast({ title: t('common.success'), description: t('forum.topicDeleted') });
      navigate('/forum');
    } catch (e) {
      toast({ title: t('common.error'), description: t('forum.deleteFailed'), variant: 'destructive' });
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      general: 'bg-gray-100 text-gray-700',
      maintenance: 'bg-orange-100 text-orange-700',
      events: 'bg-purple-100 text-purple-700',
      rules: 'bg-red-100 text-red-700',
      suggestions: 'bg-green-100 text-green-700'
    };
    return colors[category] || colors.general;
  };

  if (loading) return <div className="max-w-7xl mx-auto py-8 text-center text-gray-500">{t('forum.loadingTopic')}</div>;
  if (!topic) return <div className="max-w-7xl mx-auto py-8 text-center text-gray-500">{t('forum.topicNotFound')}</div>;

  const tags = topic.tags ? topic.tags.split(',').filter((t) => t.trim()) : [];
  const canModerate = isManager(user?.role);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      <button onClick={() => navigate('/forum')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors">
        <LuArrowLeft className="w-5 h-5" /> {t('forum.backToForum')}
      </button>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        <div className="flex items-start justify-between mb-6">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              {topic.is_pinned && <LuPin className="w-5 h-5 text-blue-600" />}
              {topic.is_locked && <LuLock className="w-5 h-5 text-gray-600" />}
              <h1 className="text-3xl font-bold text-gray-900">{topic.title}</h1>
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <span className="flex items-center gap-1"><LuUser className="w-4 h-4" /> {authorName}</span>
              <span className="flex items-center gap-1">
                <LuClock className="w-4 h-4" /> 
                {format(new Date(topic.created_at), 'MMM d, yyyy h:mm a', { locale: dateLocale })}
              </span>
              <span className="flex items-center gap-1">
                <LuMessageSquare className="w-4 h-4" /> 
                {t('forum.replies', { count: topic.reply_count || 0 })}
              </span>
            </div>
          </div>
          {canModerate &&
          <div className="flex items-center gap-2">
              <button
              onClick={handlePin}
              className={`p-2 rounded-lg transition ${topic.is_pinned ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`}
              title={topic.is_pinned ? t('forum.unpin') : t('forum.pin')}>

                <LuPin className="w-5 h-5" />
              </button>
              <button
              onClick={handleLock}
              className={`p-2 rounded-lg transition ${topic.is_locked ? 'bg-yellow-100 text-yellow-600' : 'bg-gray-100 text-gray-600'}`}
              title={topic.is_locked ? t('forum.unlock') : t('forum.lock')}>

                <LuLock className="w-5 h-5" />
              </button>
              <button
              onClick={handleDelete}
              className="p-2 bg-red-100 text-red-600 hover:bg-red-200 rounded-lg transition"
              title={t('forum.delete')}>

                <LuTrash2 className="w-5 h-5" />
              </button>
            </div>
          }
        </div>

        <div className="flex items-center gap-2 flex-wrap mb-6">
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(topic.category)}`}>
            {t(`forum.categories.${topic.category}`) || topic.category}
          </span>
          {tags.map((tag, idx) =>
          <span key={idx} className="flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
              <LuTag className="w-3 h-3" /> {tag.trim()}
            </span>
          )}
        </div>

        <div className="prose max-w-none text-gray-700 whitespace-pre-wrap leading-relaxed">
          <MentionText text={topic.body} />
        </div>
      </div>

      <ReplySection topicId={topicId} />
      
      {!topic.is_locked ?
      <ReplyForm
        topicId={topicId}
        topicTitle={topic.title}
        onReplyAdded={() => loadTopic()} /> :


      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
          <LuLock className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
          <p className="text-yellow-800 font-medium">{t('forum.topicLockedMsg')}</p>
        </div>
      }
    </div>);

}